import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useAuthContext } from 'contexts';
import { Base } from 'layout';
import { useEffect, useMemo, useState } from 'react';
import { MagentoService } from 'services';

export const DashboardPacotes = () => {
    const { categories, isAdmin, isCategoriesLoading } = useAuthContext();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [report, setReport] = useState(null);
    const [categoriaId, setCategoriaId] = useState('');
    const [selectedPackageKey, setSelectedPackageKey] = useState('');
    const cardLabelColor = '#6b7280';
    const shouldShowCategoryFilter = isAdmin || categories.length > 1;

    const formatDate = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('pt-BR');
    };

    const loadReport = () => {
        setLoading(true);
        setError('');

        MagentoService.getOperationReport({
            categoriaId: categoriaId || undefined
        }).then((result) => {
            setLoading(false);
            if (result instanceof Error) {
                setError(result.message);
            } else {
                setReport(result);
                const firstPackage = result?.pacotes_vagas?.[0];
                setSelectedPackageKey(firstPackage ? `${firstPackage.produto_id}-${firstPackage.sku || ''}` : '');
            }
        });
    };

    useEffect(() => {
        if (isCategoriesLoading) return;

        if (!categoriaId) {
            if (categories.length > 0) {
                setCategoriaId(String(categories[0].id));
            }
            return;
        }

        loadReport();
    }, [categoriaId, isCategoriesLoading, categories]);

    // useEffect(() => {
    //     if (!isCategoriesLoading && categories.length > 0 && !categoriaId) {
    //         setCategoriaId(String(categories[0].id));
    //     }
    // }, [categories, categoriaId, isCategoriesLoading]);

    // useEffect(() => {
    //     if (!isCategoriesLoading && (categoriaId || !shouldShowCategoryFilter)) {
    //         loadReport();
    //     }
    // }, [categoriaId, isCategoriesLoading, shouldShowCategoryFilter]);

    const packagesWithSlots = report?.pacotes_vagas || [];
    const selectedPackage = packagesWithSlots.find(
        (pacote) => `${pacote.produto_id}-${pacote.sku || ''}` === selectedPackageKey
    );
    const selectedPackageSlots = selectedPackage?.pacotes || [];
    const totalVagasRestantes = useMemo(() => {
        if (!packagesWithSlots.length) return 0;
        return packagesWithSlots.reduce((total, pacote) => {
            const pacoteTotal = (pacote.pacotes || []).reduce(
                (acc, periodo) => acc + Number(periodo.vagas_restantes || 0),
                0
            );
            return total + pacoteTotal;
        }, 0);
    }, [packagesWithSlots]);

    const slotsChartData = useMemo(() => {
        if (!selectedPackage) return [];
        return (selectedPackage.pacotes || []).map((periodo) => {
            const inicio = formatDate(periodo.data_inicio);
            const fim = formatDate(periodo.data_fim);
            const total = Number(periodo.total_vagas || 0);
            const restantes = Number(periodo.vagas_restantes || 0);
            const ocupadas = Number(periodo.vagas_utilizadas || 0) || Math.max(total - restantes, 0);
            return {
                label: `${inicio} - ${fim}`,
                total,
                restantes,
                ocupadas
            };
        });
    }, [selectedPackage]);

    return (
        <Base
            titulo='Pacotes'
            subTitulo='Acompanhe os indicadores de pacotes e vagas por operação.'>
            <Box
                component={Paper}
                elevation={0}
                variant='outlined'
                marginY={2}
                marginRight={2}
                padding={2}>
                <Grid
                    container
                    spacing={1}>
                    {shouldShowCategoryFilter && (
                        <Grid
                            item
                            xs={12}>
                            <Box
                                display='flex'
                                gap={2}
                                flexWrap='wrap'>
                                <TextField
                                    select
                                    label='Operação'
                                    size='small'
                                    value={categoriaId}
                                    onChange={(e) => setCategoriaId(e.target.value)}
                                    sx={{ minWidth: 200 }}
                                    disabled={isCategoriesLoading}>
                                    <MenuItem value=''>{isCategoriesLoading ? 'Carregando...' : 'Todas'}</MenuItem>
                                    {!isCategoriesLoading &&
                                        categories.map((categoria) => (
                                            <MenuItem
                                                key={categoria.id}
                                                value={String(categoria.id)}>
                                                {isAdmin ? categoria.name : categoria.nome}
                                            </MenuItem>
                                        ))}
                                </TextField>
                                <TextField
                                    select
                                    label='Pacote'
                                    size='small'
                                    value={selectedPackageKey}
                                    onChange={(e) => setSelectedPackageKey(e.target.value)}
                                    sx={{ minWidth: 220 }}>
                                    <MenuItem value=''>Selecione</MenuItem>
                                    {packagesWithSlots.map((pacote) => {
                                        const pacoteKey = `${pacote.produto_id}-${pacote.sku || ''}`;
                                        return (
                                            <MenuItem
                                                key={pacoteKey}
                                                value={pacoteKey}>
                                                {pacote.nome}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                                <Button
                                    variant='contained'
                                    disableElevation
                                    onClick={loadReport}>
                                    Buscar
                                </Button>
                            </Box>
                        </Grid>
                    )}

                    <Grid
                        item
                        xs={12}>
                        {loading && (
                            <Box
                                display='flex'
                                justifyContent='center'
                                padding={2}>
                                <CircularProgress size={44} />
                            </Box>
                        )}
                        {!loading && error && <Typography color='error'>{error}</Typography>}
                    </Grid>

                    {!loading && report && (
                        <Grid
                            item
                            xs={12}>
                            <Box sx={{ overflowX: 'auto' }}>
                                <Box
                                    display='flex'
                                    gap={2}
                                    flexWrap='nowrap'>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 170 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Total de Vagas
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                {report.total_vagas || 0}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                    <Card
                                        elevation={0}
                                        variant='outlined'
                                        sx={{ minWidth: 170 }}>
                                        <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                                            <Typography
                                                variant='body2'
                                                sx={{ color: cardLabelColor }}>
                                                Vagas Restantes
                                            </Typography>
                                            <Typography
                                                variant='h6'
                                                sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                {totalVagasRestantes}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {!loading && report && (
                    <Box
                        component={Paper}
                        elevation={0}
                        variant='outlined'
                        marginTop={4}
                        marginRight={2}
                        padding={1.5}>
                        <Typography variant='h6'>Vagas por Período</Typography>
                        <Divider sx={{ marginY: 1.5 }} />
                        {!selectedPackage && (
                            <Typography variant='body2'>Selecione um pacote para visualizar o período.</Typography>
                        )}
                        {selectedPackage && slotsChartData.length === 0 && (
                            <Typography variant='body2'>Nenhum período encontrado para este pacote.</Typography>
                        )}
                        {selectedPackage && slotsChartData.length > 0 && (
                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Box sx={{ minWidth: 760 }}>
                                    <BarChart
                                        height={320}
                                        xAxis={[
                                            {
                                                id: 'pacotes',
                                                data: slotsChartData.map((item) => item.label),
                                                scaleType: 'band'
                                            }
                                        ]}
                                        yAxis={[
                                            {
                                                valueFormatter: (value) => `${value || 0}`
                                            }
                                        ]}
                                        series={[
                                            {
                                                label: 'Vagas Ocupadas',
                                                color: '#d97706',
                                                data: slotsChartData.map((item) => item.ocupadas),
                                                stack: 'vagas'
                                            },
                                            {
                                                label: 'Vagas Restantes',
                                                color: '#fbbf24',
                                                data: slotsChartData.map((item) => item.restantes),
                                                stack: 'vagas'
                                            }
                                        ]}
                                        tooltip={{ trigger: 'item' }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {!loading && report && (
                    <Grid
                        container
                        spacing={2}
                        marginY={1}
                        marginRight={2}
                        columnSpacing={3}>
                        <Grid
                            item
                            xs={12}
                            lg={6}>
                            <Box
                                component={Paper}
                                elevation={0}
                                variant='outlined'
                                padding={1}>
                                <Box
                                    display='flex'
                                    alignItems='center'
                                    justifyContent='space-between'
                                    flexWrap='wrap'
                                    gap={2}>
                                    <Typography variant='h6'>Pacotes com Vagas</Typography>
                                    {/* <TextField
                                        select
                                        label='Pacote'
                                        size='small'
                                        value={selectedPackageKey}
                                        onChange={(e) => setSelectedPackageKey(e.target.value)}
                                        sx={{ minWidth: 220 }}>
                                        <MenuItem value=''>Selecione</MenuItem>
                                        {packagesWithSlots.map((pacote) => {
                                            const pacoteKey = `${pacote.produto_id}-${pacote.sku || ''}`;
                                            return (
                                                <MenuItem
                                                    key={pacoteKey}
                                                    value={pacoteKey}>
                                                    {pacote.nome}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField> */}
                                </Box>
                                <Divider sx={{ marginY: 1 }} />
                                {packagesWithSlots.length === 0 && (
                                    <Typography variant='body2'>Nenhum pacote encontrado.</Typography>
                                )}
                                {packagesWithSlots.length > 0 && !selectedPackage && (
                                    <Typography variant='body2'>Selecione um pacote para ver as vagas.</Typography>
                                )}
                                {selectedPackage && selectedPackageSlots.length === 0 && (
                                    <Typography variant='body2'>Nenhuma vaga cadastrada para este pacote.</Typography>
                                )}
                                {selectedPackage && selectedPackageSlots.length > 0 && (
                                    <Table size='small'>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Data Inicio</TableCell>
                                                <TableCell>Data Fim</TableCell>
                                                <TableCell>Vagas Totais</TableCell>
                                                <TableCell>Vagas Restantes</TableCell>
                                                <TableCell>Vagas Utilizadas</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedPackageSlots.map((periodo, index) => (
                                                <TableRow key={periodo.periodo_id || `${selectedPackageKey}-${index}`}>
                                                    <TableCell>{formatDate(periodo.data_inicio)}</TableCell>
                                                    <TableCell>{formatDate(periodo.data_fim)}</TableCell>
                                                    <TableCell>{periodo.total_vagas ?? 0}</TableCell>
                                                    <TableCell>{periodo.vagas_restantes ?? 0}</TableCell>
                                                    <TableCell>{periodo.vagas_utilizadas ?? 0}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Base>
    );
};
